import { useState } from "react";
import { Breadcrumb, buildCurl, buildFetch, CodeBlock, CopyButton, findVariables, flattenFields, methodOf, MethodTag, parseBody, Section, urlOf, VarText } from "./PostmanVIewHelper";

function EndpointView({ node, tree, onSelect }) {
  const [lang, setLang] = useState("curl");
  const req = node.request;
  const method = methodOf(node);
  const headers = (req.header || []).filter((h) => !h.disabled);
  const body = parseBody(req.body);
  const fields = body?.ok ? flattenFields(body.value) : [];
  const vars = findVariables(node);
  const bodyPretty = body ? (body.ok ? JSON.stringify(body.value, null, 2) : body.raw) : "";

  return (
    <div className="ad-main-inner">
      <Breadcrumb path={node.path} onSelect={onSelect} tree={tree} />
      <h1>{node.name}</h1>

      <div className="ad-endpoint">
        <MethodTag method={method} />
        <code className="ad-url">
          <VarText text={urlOf(node)} />
        </code>
        <CopyButton text={urlOf(node)} label="Copy URL" />
      </div>

      {vars.length > 0 && (
        <p className="ad-vars">
          Variable:{" "}
          {vars.map((v) => (
            <span key={v} className="ad-var">{v}</span>
          ))}
        </p>
      )}

      <Section title="Headers" hint={`${headers.length} sent`}>
        {headers.length === 0 ? (
          <p className="ad-none">This request sends no headers.</p>
        ) : (
          <div className="ad-table-wrap">
            <table className="ad-table">
              <thead>
                <tr><th>Name</th><th>Value</th></tr>
              </thead>
              <tbody>
                {headers.map((h, i) => (
                  <tr key={i}>
                    <td className="ad-mono">{h.key}</td>
                    <td className="ad-mono"><VarText text={h.value} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <Section title="Request body">
        {!body ? (
          <p className="ad-none">
            {method === "GET" || method === "DELETE"
              ? "No body. The request is identified by its URL."
              : "This request has no body."}
          </p>
        ) : (
          <>
            {fields.length > 0 && (
              <div className="ad-table-wrap">
                <table className="ad-table">
                  <thead>
                    <tr><th>Field</th><th>Type</th><th>Example</th></tr>
                  </thead>
                  <tbody>
                    {fields.map((f) => {
                      const depth = f.path.split(".").length - 1;
                      const leaf = f.path.split(".").pop();
                      return (
                        <tr key={f.path}>
                          <td className="ad-mono" style={{ paddingLeft: 14 + depth * 16 }}>
                            {leaf}
                          </td>
                          <td><span className="ad-type">{f.type}</span></td>
                          <td className="ad-mono ad-example">{f.example}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            <CodeBlock title="JSON" code={bodyPretty} json={body.ok} />
          </>
        )}
      </Section>

      <Section title="Code sample">
        <div className="ad-tabs" role="tablist">
          {[["curl", "cURL"], ["fetch", "JavaScript"]].map(([k, l]) => (
            <button
              key={k}
              type="button"
              role="tab"
              aria-selected={lang === k}
              className={lang === k ? "is-active" : ""}
              onClick={() => setLang(k)}
            >
              {l}
            </button>
          ))}
        </div>
        <CodeBlock
          title={lang === "curl" ? "cURL" : "fetch"}
          code={lang === "curl" ? buildCurl(node) : buildFetch(node)}
        />
      </Section>

      {node.response?.length > 0 && (
        <Section title="Example responses">
          {node.response.map((r, i) => (
            <CodeBlock
              key={i}
              title={`${r.name || "Response"}${r.code ? ` · ${r.code}` : ""}`}
              code={r.body || ""}
              json
            />
          ))}
        </Section>
      )}
    </div>
  );
}

export default EndpointView